import tokens from "@/design-system/tokens.json";

/**
 * Tasks Module v1.1 § 2.3 — the Phoenix design tokens an admin can pick from
 * when creating or editing a tag. The free hex picker is intentionally NOT
 * exposed; admin selections are constrained to this palette so brand voice
 * stays consistent across the platform.
 *
 * The `token` string is what we persist (`TaskTag.Color`); the `hex` is only
 * what the admin swatch and preview dot paint. THE CHIP DOES NOT USE THE HEX —
 * `pages/tasks/components/TaskTagChips.tsx` maps the token to a Tailwind class,
 * and that map is the real renderer. So this file's job is to show the admin
 * the colour their tag is about to become.
 *
 * 🔴 WHY THERE ARE SEVEN AND NOT EIGHT. An eighth swatch, `indigo.DEFAULT`,
 * used to sit at the bottom of this list. There is no `indigo` token in
 * `tokens.json` and no `indigo.DEFAULT` arm in `TaskTagChips`, so picking it
 * persisted a token nothing could resolve: the admin chose a violet swatch and
 * got the neutral outline chip. The palette's real ceiling is set by
 * `TaskTagChips.TOKEN_TO_CLASS` — a swatch offered here that the chip cannot
 * render is a broken promise, not a colour. **To add an eighth colour, add the
 * arm THERE first, then add the swatch here.**
 *
 * 🔴 AND WHY THE HEXES ARE RESOLVED, NOT TYPED. All seven were hardcoded, and
 * all seven were wrong — `gold.DEFAULT` was written `#C9A227` when brand gold
 * is `#C9A961`, `gold.shadow` was `#5A4E1B` against a real `#8B6F2E`, and so on
 * down the list. Each cited the right token name beside the wrong value, which
 * is the hardest kind of mistake to find: an anonymous hex at least looks like
 * something to check, whereas a hex captioned `gold.DEFAULT` reads as already
 * verified. Nobody was going to grep it. Both halves of a row now come from one
 * `swatch()` call, so the name and the value cannot disagree, and a token that
 * does not exist — `indigo` among them — fails the typecheck instead of
 * shipping a swatch that paints nothing.
 */
export interface TagColor {
  token: string;
  label: string;
  hex: string;
}

type ColorGroups = typeof tokens.colors;

/**
 * Builds one swatch from a design token, deriving the persisted token string
 * and the painted hex from the SAME lookup. The generic parameters are the
 * guard: `group` must name a real group in `tokens.json` and `key` a real entry
 * within it, so a phantom token is a compile error at the point it is written.
 */
function swatch<G extends keyof ColorGroups>(
  group: G,
  key: keyof ColorGroups[G] & string,
  label: string,
): TagColor {
  return {
    token: `${String(group)}.${key}`,
    label,
    hex: (tokens.colors[group] as Record<string, string>)[key],
  };
}

/**
 * Order is the swatch row's left-to-right order and is deliberately stable —
 * `TAG_COLORS[0]` is the default for a new tag and the fallback for a tag whose
 * persisted token this palette no longer offers.
 */
export const TAG_COLORS: readonly TagColor[] = [
  swatch("gold", "DEFAULT", "Gold"),
  // Was labelled "Onyx accent", which was roughly honest only while the swatch
  // painted the wrong near-black `#5A4E1B`. Against the real `#8B6F2E` it is a
  // bronze, and a label naming a colour the dot is not is the same defect as a
  // hex naming a token it is not.
  swatch("gold", "shadow", "Bronze"),
  swatch("status", "info", "Slate"),
  swatch("status", "success", "Emerald"),
  swatch("status", "warning", "Amber"),
  swatch("status", "error", "Ruby"),
  swatch("gray", "mid", "Stone"),
] as const;

export const DEFAULT_TAG_COLOR_TOKEN = TAG_COLORS[0].token;

/**
 * A tag saved before this palette changed can carry a token no longer offered
 * — the API validates only the length of `TaskTag.Color`, never its membership
 * — so an unresolvable token falls back to the default swatch rather than
 * painting `undefined`. The chip already degrades the same way, to its neutral
 * outline.
 */
export function tagColorHex(token: string | null | undefined): string {
  if (!token) return TAG_COLORS[0].hex;
  return TAG_COLORS.find((c) => c.token === token)?.hex ?? TAG_COLORS[0].hex;
}
